import { css, html, LitElement, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../icon/icon.element';
import { IconType } from '../icon/icon.types';

export type MenuPosition = 'bottom-left' | 'bottom-right';

@customElement('cds-menu')
export class MenuElement extends LitElement {
  static styles = css`
    :host {
      display: inline-block;
      position: relative;
    }

    :host([open]) {
      .cds-icon {
        color: var(--token-component-overflow-menu-button-pressed-icon, light-dark(#0174bc, #7dcdff));
      }
    }

    .menu--container {
      background: var(--token-component-dropdown-background-default, light-dark(#ffffff, #343438));
      box-shadow: var(--token-alias-elevation-s, 0 2px 8px 0 light-dark(rgba(58,59,63,0.18), rgba(0,0,0,0.18)));
      display: flex;
      flex-direction: column;
      gap: var(--token-global-spacing-s4, 4px);
      padding: var(--token-global-spacing-s4, 4px) 0px;
      position: absolute;
      z-index: 1000;
    }

    .menu--container.bottom-left {
      left: 0;
      right: auto;
      top: 100%;
    }

    .menu--container.bottom-right {
      left: auto;
      right: 0;
      top: 100%;
    }
  `;

  @property({ attribute: 'aria-expanded', type: String, reflect: true })
  ariaExpanded = 'false';

  @property({ attribute: 'aria-haspopup', type: String, reflect: true })
  ariaHasPopup = 'menu';

  @property({ type: String })
  icon: IconType = 'actions';

  @property({ type: String })
  label: string = '';

  @property({ type: Boolean, reflect: true })
  open = false;

  @property({ type: String, reflect: true })
  position: MenuPosition = 'bottom-left';

  @property({ type: String, reflect: true })
  role = 'menu';

  @property({ type: String, reflect: true })
  tabindex = '0';

  constructor() {
    super();
    this.handleSelect = this.handleSelect.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    if (changedProperties.has('open')) {
      if (this.open) {
        setTimeout(() => {
          this.addEventListeners();
        }, 0);
      } else {
        this.removeEventListeners();
      }
    }
  }

  render() {
    if (this.open) {
      this.ariaExpanded = 'true';
    } else {
      this.ariaExpanded = 'false';
    }

    return html`
      <label>${this.label}</label>
      <cds-icon
        icon=${this.icon}
        tabindex="0"
        @click=${() => (this.open = !this.open)}
        @keydown=${this.handleKeyDown}
      ></cds-icon>
      ${this.open ? this.renderMenuContainer() : ''}
    `;
  }

  private addEventListeners(): void {
    this.addEventListener('select', this.handleSelect);
    document.addEventListener('click', this.handleClickOutside);
  }

  private handleClickOutside(e: MouseEvent): void {
    if (this.open && !this.contains(e.target as Node)) {
      this.open = false;
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ' ') {
      this.open = !this.open;
    }
  }

  private handleSelect(e: Event): void {
    this.open = false;
  }

  private removeEventListeners(): void {
    this.removeEventListener('select', this.handleSelect);
    document.removeEventListener('click', this.handleClickOutside);
  }
  
  private renderMenuContainer(): TemplateResult {
    const classes = {
      'menu--container': true,
      active: this.open,
      'bottom-left': this.position === 'bottom-left',
      'bottom-right': this.position === 'bottom-right',
    };

    return html`
      <div class="${classMap(classes)}">
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cds-menu': MenuElement;
  }
}
